import type { Area, Grade, Lift, Run } from '../../shared/contract'

/**
 * Counting the mountain: per area, per grade, under a filter. Pure, so the
 * page and its tests count the same way.
 *
 * A run with no grade (the report used a word this page doesn't know) is
 * counted in every total and in no grade column, and a grade filter never
 * matches it.
 */

export const GRADES = ['green', 'blue', 'black', 'double'] as const satisfies readonly Grade[]
export const STATUSES = ['open', 'groomed', 'closed'] as const
export type Status = (typeof STATUSES)[number]

export const GRADE_NAME: Record<Grade, string> = {
  green: 'green',
  blue: 'blue',
  black: 'black',
  double: 'double black',
}
const GRADE_ADJ: Record<Grade, string> = {
  green: 'green',
  blue: 'blue',
  black: 'black',
  double: 'double-black',
}

export interface Filter {
  status: Status | null
  grades: readonly Grade[]
}

export const isGrade = (value: unknown): value is Grade => GRADES.includes(value as Grade)
export const isStatus = (value: unknown): value is Status => STATUSES.includes(value as Status)

/** A lift turns only when the report says plainly that it is open; a hold is a stop. */
export const turning = (lift: Lift) => lift.open && !lift.status

export const filtering = (f: Filter) => f.status != null || f.grades.length > 0

/** Open includes groomed. */
export function statusOk(run: Run, status: Status | null): boolean {
  if (!status) return true
  if (status === 'open') return run.open
  if (status === 'groomed') return run.groomed
  return !run.open
}

/** None selected means all; an ungraded run matches only then. */
export const gradeOk = (run: Run, grades: readonly Grade[]) =>
  grades.length === 0 || (run.grade != null && grades.includes(run.grade))

export const matches = (run: Run, f: Filter) => statusOk(run, f.status) && gradeOk(run, f.grades)

const gradeRank = (run: Run) => (run.grade ? GRADES.indexOf(run.grade) : GRADES.length)
const stateRank = (run: Run) => (run.groomed ? 0 : run.open ? 1 : 2)
const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, 'en', { numeric: true })

/** By grade (ungraded last), then groomed, open, closed, then by name. */
export const sortRuns = (runs: readonly Run[]) =>
  runs.toSorted(
    (a, b) => gradeRank(a) - gradeRank(b) || stateRank(a) - stateRank(b) || byName(a, b),
  )

export interface Count {
  open: number
  total: number
}

export const count = (runs: readonly Run[]): Count => ({
  open: runs.filter((r) => r.open).length,
  total: runs.length,
})

export interface GradeCount extends Count {
  grade: Grade
}

/** One entry per grade the runs actually have, in sign order. */
export const byGrade = (runs: readonly Run[]): GradeCount[] =>
  GRADES.flatMap((grade) => {
    const of = runs.filter((r) => r.grade === grade)
    return of.length ? [{ grade, ...count(of) }] : []
  })

export interface GradeGroup {
  /** Null for the runs the report gave no known grade. */
  grade: Grade | null
  runs: Run[]
}

/** Runs grouped under their grade, each group sorted groomed, open, closed. */
export function groups(runs: readonly Run[]): GradeGroup[] {
  const out: GradeGroup[] = []
  for (const grade of [...GRADES, null]) {
    const of = runs.filter((r) => (r.grade ?? null) === grade)
    if (of.length) out.push({ grade, runs: sortRuns(of) })
  }
  return out
}

export interface Mountain {
  lifts: Lift[]
  runs: Run[]
  turning: number
  open: number
  groomed: number
  /** Anything at all running: when false, plates show plain totals. */
  anyOpen: boolean
}

export function mountain(areas: readonly Area[]): Mountain {
  const lifts = areas.flatMap((a) => a.lifts)
  const runs = areas.flatMap((a) => a.runs)
  const turningLifts = lifts.filter(turning).length
  const open = runs.filter((r) => r.open).length
  return {
    lifts,
    runs,
    turning: turningLifts,
    open,
    groomed: runs.filter((r) => r.groomed).length,
    anyOpen: open > 0 || turningLifts > 0,
  }
}

/** Nothing running here while the rest of the mountain is: the plate folds to one line. */
export const dead = (area: Area, anyOpen: boolean) =>
  anyOpen && !area.runs.some((r) => r.open) && !area.lifts.some(turning)

export interface Exceptions {
  /** The word before the names: the few closed in an open area, or the few open in a closed one. */
  kind: 'closed' | 'open'
  runs: Run[]
}

/**
 * Name the exceptions: 1 to 3 closed runs in an area that is otherwise open,
 * or 1 to 3 open runs in an area that is mostly closed.
 */
export function exceptions(area: Area, anyOpen: boolean): Exceptions | null {
  if (!anyOpen) return null
  const open = area.runs.filter((r) => r.open)
  const closed = area.runs.filter((r) => !r.open)
  if (open.length && closed.length >= 1 && closed.length <= 3)
    return { kind: 'closed', runs: sortRuns(closed) }
  if (open.length >= 1 && open.length <= 3 && closed.length > 3)
    return { kind: 'open', runs: sortRuns(open) }
  return null
}

/** The one area with anything open, when there is exactly one (early season): it names its open runs. */
export function loneArea(areas: readonly Area[]): string | null {
  const open = areas.filter((a) => a.runs.some((r) => r.open))
  return open.length === 1 ? open[0]!.name : null
}

export interface DockCounts {
  /** Per status (null = all), for the selected grades: the lit number is the match count. */
  status: (status: Status | null) => number
  /** Per grade, for the selected status. */
  grade: (grade: Grade) => number
}

export function dockCounts(runs: readonly Run[], f: Filter): DockCounts {
  const graded = runs.filter((r) => gradeOk(r, f.grades))
  const stated = runs.filter((r) => statusOk(r, f.status))
  return {
    status: (status) => graded.filter((r) => statusOk(r, status)).length,
    grade: (grade) => stated.filter((r) => r.grade === grade).length,
  }
}

const andList = (xs: string[]) =>
  xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} & ${xs[xs.length - 1]}`

/** "12 groomed black & double-black runs". */
export function filterTitle(n: number, f: Filter): string {
  const grades = GRADES.filter((g) => f.grades.includes(g)).map((g) => GRADE_ADJ[g])
  const words = [f.status ?? '', andList(grades)].filter(Boolean).join(' ')
  return `${n} ${words} run${n === 1 ? '' : 's'}`
}

/** Where the matches are: [area, count] for each area with any. */
export const perArea = (areas: readonly Area[], f: Filter): [string, number][] =>
  areas.flatMap((a) => {
    const n = a.runs.filter((r) => matches(r, f)).length
    return n ? [[a.name, n] as [string, number]] : []
  })

export interface LiftsLine {
  label: string
  /** Named lifts, or null when the text says all of them. */
  names: string[] | null
  text: string
}

/** Short phones: name whichever list is shorter, the stopped lifts or the turning ones. */
export function liftsLine(lifts: readonly Lift[]): LiftsLine {
  const on = lifts.filter(turning)
  const off = lifts.filter((l) => !turning(l))
  if (!on.length) return { label: 'Lifts', names: null, text: `all ${lifts.length} stopped` }
  if (!off.length) return { label: 'Lifts', names: null, text: `all ${lifts.length} turning` }
  const closed = off.length <= on.length
  const selected = closed ? off : on
  const carpetTotal = lifts.filter(isCarpet).length
  const carpetSelected = selected.filter(isCarpet).length
  const names = liftRows(selected).map((row) =>
    row.name === 'Carpets' && carpetSelected !== carpetTotal
      ? `Carpets (${carpetSelected}/${carpetTotal})`
      : row.name,
  )
  return { label: closed ? 'Lifts closed' : 'Lifts turning', names, text: names.join(' · ') }
}

/** A run's state in words: open or closed, and groomed whichever it is. */
export const runState = (run: Run) =>
  `${run.open ? 'open' : 'closed'}${run.groomed ? ', groomed' : ''}`

/** What a screen reader hears for a run. */
export function runLabel(run: Run): string {
  const grade = run.grade ? GRADE_NAME[run.grade] : 'ungraded'
  return `${run.name}, ${grade}, ${runState(run)}`
}

export function liftLabel(lift: Lift): string {
  return `${lift.name}, ${lift.status ? lift.status.toLowerCase() : turning(lift) ? 'turning' : 'stopped'}`
}

export interface LiftRow {
  name: string
  turning: boolean
  status?: string
  label: string
}
export const isCarpet = (lift: Lift) => /\bcarpet\b/i.test(lift.name)

/** One display row for the carpets; physical lift totals and report IDs stay intact. */
export function liftRows(lifts: readonly Lift[]): LiftRow[] {
  const carpets = lifts.filter(isCarpet)
  const first = lifts.findIndex(isCarpet)
  const running = carpets.filter(turning).length
  return lifts.flatMap((lift, index) => {
    if (!isCarpet(lift))
      return [
        { name: lift.name, turning: turning(lift), status: lift.status, label: liftLabel(lift) },
      ]
    if (index !== first) return []
    return [
      {
        name: 'Carpets',
        turning: running > 0,
        status:
          (running > 0 && running < carpets.length) || carpets.some((l) => l.status)
            ? `${running}/${carpets.length} open`
            : undefined,
        label: `Carpets, ${running} of ${carpets.length} turning: ${carpets.map(liftLabel).join('; ')}`,
      },
    ]
  })
}
